import { DayOfWeek } from '@weareopenr/types';
import { IconButton, MenuIcon, PriceBandIcon, Plus, Tile, TileVariant } from '@weareopenr/component-library';
import { ITimetableDay, ITimetableDaySchedule } from 'src/utils/timetables/groupSchedulesByDay';
import { toGridTemplateColumns, TimetableEntityType } from 'src/utils/timetables/helpers';
import { getDayLabel } from 'src/helpers/day/day';
import { TimetableView } from 'src/pages/Timetable/Timetable';
import styles from './TimetableDay.module.scss';

export interface TimetableDayProps {
  day: ITimetableDay;
  view: TimetableView;
  onAddSchedule: (entityType: TimetableEntityType, day: DayOfWeek) => void;
  isActionable: boolean;
}

interface ITimetableLane {
  key: string;
  Icon: typeof MenuIcon;
  items: ITimetableDaySchedule[];
  variant: TileVariant;
  addLabel: string;
  entityType: TimetableEntityType;
  hidden: boolean;
}

export const TimetableDay = ({ day, view, onAddSchedule, isActionable }: TimetableDayProps) => {
  const lanes: ITimetableLane[] = [
    {
      key: 'menu',
      Icon: MenuIcon,
      items: day.menus,
      variant: 'muted',
      addLabel: 'Add menu schedule',
      entityType: 'menu',
      hidden: view === TimetableView.PRICING_ONLY,
    },
    {
      key: 'price',
      Icon: PriceBandIcon,
      items: day.priceSchemes,
      variant: 'bright',
      addLabel: 'Add price scheme schedule',
      entityType: 'priceScheme',
      hidden: view === TimetableView.MENUS_ONLY,
    },
  ];

  return (
    <div className={styles['timetable-day']}>
      <div className={styles['timetable-day-label']}>{getDayLabel(day.day)}</div>
      <div className={styles['timetable-day-rows']}>
        {lanes
          .filter((lane) => !lane.hidden)
          .map(({ key, Icon, items, variant, addLabel, entityType }) => (
            <div key={key} className={styles['timetable-day-row']}>
              <div className={styles['timetable-day-icon']}>
                <Icon size="small" />
              </div>
              <div
                className={styles['timetable-day-tiles']}
                style={{ gridTemplateColumns: toGridTemplateColumns(items) }}
              >
                {items.map((item) => (
                  <Tile
                    key={item.scheduleId}
                    mainText={item.name}
                    secondaryText={`${item.startTime} - ${item.endTime}`}
                    colour={item.colour}
                    variant={variant}
                    hasAccent
                    label={item.isDefault ? 'Default' : undefined}
                  />
                ))}
              </div>
              {isActionable && (
                <div className={styles['timetable-day-add-button']}>
                  <IconButton
                    icon={Plus}
                    aria-label={addLabel}
                    buttonStyle="outline"
                    onClick={() => onAddSchedule(entityType, day.day)}
                  />
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};
